import React, { useState, useEffect } from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  UseFloatingData,
} from "@floating-ui/react";

export default function Tooltip({
  children,
  text,
  placement = "bottom",
}: {
  text: string;
  children: JSX.Element;
  placement?: UseFloatingData["placement"];
}) {
  const [open, setOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    middleware: [offset(10), flip(), shift()],
    whileElementsMounted: autoUpdate,
    placement: placement,
  });

  const hover = useHover(context, {
    delay: { open: 200 },
  });

  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, {
    role: "tooltip",
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  return (
    <>
      {React.Children.map(children, (child) =>
        React.cloneElement(child, {
          ref: refs.setReference,
          ...getReferenceProps(),
        })
      )}
      {open && (
        <div
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            opacity: 1,
            transition: "opacity 200ms ease-in-out",
          }}
          className="min-w-32 max-w-48 w-fit bg-gray-700 text-white text-sm rounded py-2 px-3 bg-opacity-90 z-50"
          {...getFloatingProps()}
        >
          <p>{text}</p>
        </div>
      )}
    </>
  );
}
